import { useState, useRef, useEffect } from 'react';
import { login, verifyMfa } from '../services/portalApi';

function LogoMark() {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 54, height: 54, borderRadius: 14,
      background: 'linear-gradient(135deg, var(--brand-dark), var(--brand))',
      marginBottom: 14,
    }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="var(--on-brand)"/>
      </svg>
    </div>
  );
}

export default function Login({ onLogin }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [code,     setCode]     = useState('');
  const [step,     setStep]     = useState('credentials'); // 'credentials' | 'mfa'
  const [error,    setError]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (step === 'mfa') codeRef.current?.focus();
  }, [step]);

  async function handleCredentials(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.mfaRequired) {
        setMfaToken(data.mfaToken);
        setStep('mfa');
      } else {
        localStorage.setItem('portal_token', data.token);
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMfa(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await verifyMfa(mfaToken, code.replace(/\s/g, ''));
      localStorage.setItem('portal_token', data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  const wrap = (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <LogoMark />
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '-0.5px' }}>DamovoHosting</div>
          <div style={{ fontSize: 13, color: 'var(--muted-3)', marginTop: 4 }}>
            {step === 'mfa' ? 'Two-factor authentication' : 'Sign in to your account'}
          </div>
        </div>

        <div className="card">
          {step === 'credentials' ? (
            <form onSubmit={handleCredentials} style={{ padding: '24px 24px 20px' }}>
              {error && <ErrorBanner msg={error} />}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input" type="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoFocus
                />
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Password</label>
                <input
                  className="form-input" type="password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={loading}
                style={{ width: '100%', justifyContent: 'center', padding: '10px 0' }}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfa} style={{ padding: '24px 24px 20px' }}>
              {error && <ErrorBanner msg={error} />}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, margin: '0 auto 12px',
                  background: 'var(--purple-bg)', border: '1px solid var(--purple)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="11" width="14" height="10" rx="2" stroke="var(--info)" strokeWidth="1.5"/>
                    <path d="M8 11V7a4 4 0 118 0v4" stroke="var(--info)" strokeWidth="1.5"/>
                    <circle cx="12" cy="16" r="1.5" fill="var(--info)"/>
                  </svg>
                </div>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                  Enter the 6-digit code from your authenticator app
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <input
                  ref={codeRef}
                  className="form-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9 ]*"
                  maxLength={7}
                  value={code}
                  onChange={e => {
                    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    setCode(v);
                  }}
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: 22, letterSpacing: 8, fontFamily: 'monospace' }}
                  required
                />
              </div>
              <button className="btn btn-primary" type="submit"
                disabled={loading || code.length < 6}
                style={{ width: '100%', justifyContent: 'center', padding: '10px 0' }}>
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(null); setCode(''); setMfaToken(null); }}
                style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', fontSize: 13, padding: '6px 0' }}
              >
                ← Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return wrap;
}

function ErrorBanner({ msg }) {
  return (
    <div style={{
      padding: '10px 14px', marginBottom: 16, borderRadius: 8,
      background: 'var(--err-bg)', border: '1px solid var(--err-border)', color: 'var(--err)',
      fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" stroke="var(--err)" strokeWidth="1.5"/>
        <path d="M12 8v4M12 16h.01" stroke="var(--err)" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      {msg}
    </div>
  );
}
